import React, { useState } from 'react';
import { FaDumbbell, FaPlus, FaMinus, FaClock, FaCalendarAlt, FaList } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from "services/api";
import useStore from "services/store";
import { useNavigate } from 'react-router-dom';

// Main CreateExercisePlan component that combines all sub-components
const CreateExercisePlan = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [preferences, setPreferences] = useState({
    type: '',
    sessions_per_week: 3,
    session_duration: 60,
    equipment: [],
    fitness_level: 'intermediate',
    goals: [],
    additional_info: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const steps = [
    { component: Type, title: "Workout Type", validate: Type.validate },
    { component: Equipment, title: "Available Equipment", validate: Equipment.validate },
    { component: Schedule, title: "Training Schedule", validate: Schedule.validate },
    { component: Preferences, title: "Additional Preferences", validate: Preferences.validate }
  ];

  const handleNext = async (e) => {
    e.preventDefault();
    const error = steps[currentStep - 1].validate(preferences);
    if (error) {
      toast.error(error);
      return;
    }

    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      try {
        setIsLoading(true);
        const response = await api.post('/exercise-plans/create', { preferences });
        if (!response.ok) {
          toast.error(response.message || 'Failed to generate exercise plan');
        } 
        toast.success('Exercise plan generated successfully!');
        navigate('/exercise-dashboard');
      } catch (error) {
        toast.error(error.message || 'Failed to generate exercise plan');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 text-center">Create Your Exercise Plan</h1>
          <div className="mt-4 text-center text-sm text-gray-600">
            Step {currentStep} of {steps.length}: {steps[currentStep - 1].title}
          </div>
        </div>

        <form onSubmit={handleNext} className="space-y-6">
          <div className="mb-8">
            <CurrentStepComponent
              preferences={preferences}
              onPreferencesChange={(newPrefs) => setPreferences({ ...preferences, ...newPrefs })}
            />
          </div>

          <div className="flex justify-between mt-8">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Previous
              </button>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="ml-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Generating...
                </div>
              ) : (
                currentStep === steps.length ? 'Generate Plan' : 'Next'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Type Component - Allows users to select their preferred workout type
const Type = ({ preferences, onPreferencesChange }) => {
  const workoutTypes = [
    { id: 'home', label: 'Home Workout', description: 'Exercises that can be done at home with minimal equipment' },
    { id: 'gym', label: 'Gym Workout', description: 'Full access to a gym with varied equipment' },
    { id: 'calisthenics', label: 'Calisthenics', description: 'Bodyweight exercises focused on strength and mobility' },
    { id: 'hybrid', label: 'Hybrid', description: 'Mix of different training styles' }
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <FaDumbbell className="mr-2" /> Choose Your Workout Type
      </h2>
      <p className="text-sm text-gray-600">Select the workout type that best fits your preferences</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {workoutTypes.map(type => (
          <div
            key={type.id}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              preferences.type === type.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-200'
            }`}
            onClick={() => onPreferencesChange({ type: type.id })}
          >
            <h3 className="font-medium text-gray-900">{type.label}</h3>
            <p className="text-sm text-gray-500 mt-1">{type.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

Type.validate = (preferences) => {
  if (!preferences.type) {
    return 'Select a workout type';
  }
  return null;
};

// Equipment Component - Allows users to select available equipment
const Equipment = ({ preferences, onPreferencesChange }) => {
  const equipmentOptions = {
    'Cardio Equipment': ['Treadmill', 'Exercise Bike', 'Rowing Machine', 'Jump Rope'],
    'Weights': ['Dumbbells', 'Barbell', 'Kettlebell', 'Weight Plates'],
    'Bodyweight': ['Pull-up Bar', 'Resistance Bands', 'Yoga Mat', 'Foam Roller'],
    'Machines': ['Cable Machine', 'Smith Machine', 'Leg Press', 'Chest Press']
  };

  const handleChange = (item, checked) => {
    const newEquipment = checked
      ? [...preferences.equipment, item.toLowerCase()]
      : preferences.equipment.filter(e => e !== item.toLowerCase());
    onPreferencesChange({ equipment: newEquipment });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <FaDumbbell className="mr-2" /> Available Equipment
      </h2>
      <p className="text-sm text-gray-600">Select the equipment you have access to</p>
      
      {Object.entries(equipmentOptions).map(([category, items]) => (
        <div key={category} className="space-y-3">
          <h3 className="font-medium text-gray-700">{category}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {items.map(item => (
              <label key={item} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preferences.equipment.includes(item.toLowerCase())}
                  onChange={(e) => handleChange(item, e.target.checked)}
                  className="rounded text-blue-500 focus:ring-blue-500"
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

Equipment.validate = (preferences) => {
  if (!preferences.equipment || preferences.equipment.length === 0) {
    return 'Select at least one piece of equipment';
  }
  return null;
};

// Schedule Component - Allows users to set their schedule preferences
const Schedule = ({ preferences, onPreferencesChange }) => {
  const handleSessionsChange = (change) => {
    const newValue = Math.min(Math.max(2, preferences.sessions_per_week + change), 7);
    onPreferencesChange({ sessions_per_week: newValue });
  };

  const handleDurationChange = (change) => {
    const newValue = Math.min(Math.max(15, preferences.session_duration + change), 120);
    onPreferencesChange({ session_duration: newValue });
  };

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <FaCalendarAlt className="mr-2" /> Training Schedule
      </h2>

      {/* Sessions per week */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Sessions per Week
        </label>
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => handleSessionsChange(-1)}
            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            <FaMinus className="text-gray-600" />
          </button>
          <span className="text-xl font-semibold">{preferences.sessions_per_week}</span>
          <button
            type="button"
            onClick={() => handleSessionsChange(1)}
            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            <FaPlus className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Session duration */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Session Duration (minutes)
        </label>
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={() => handleDurationChange(-15)}
            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            <FaMinus className="text-gray-600" />
          </button>
          <span className="text-xl font-semibold">{preferences.session_duration}</span>
          <button
            type="button"
            onClick={() => handleDurationChange(15)}
            className="p-2 rounded-full bg-gray-200 hover:bg-gray-300"
          >
            <FaPlus className="text-gray-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

Schedule.validate = (preferences) => {
  if (preferences.sessions_per_week < 2 || preferences.sessions_per_week > 7) {
    return 'Sessions per week must be between 2 and 7';
  }
  if (preferences.session_duration < 15 || preferences.session_duration > 120) {
    return 'Session duration must be between 15 and 120 minutes';
  }
  return null;
};

// Preferences Component - Allows users to set additional preferences
const Preferences = ({ preferences, onPreferencesChange }) => {
  const fitnessLevels = [
    { id: 'beginner', label: 'Beginner', description: 'New to training or returning after a long break' },
    { id: 'intermediate', label: 'Intermediate', description: 'Regular experience with good technique' },
    { id: 'advanced', label: 'Advanced', description: 'Experienced with challenging workouts' }
  ];

  const goalOptions = [
    'Strength', 'Muscle Gain', 'Weight Loss', 'Endurance',
    'Flexibility', 'General Fitness', 'Athletic Performance'
  ];

  const handleGoalChange = (goal, checked) => {
    const newGoals = checked
      ? [...preferences.goals, goal.toLowerCase()]
      : preferences.goals.filter(g => g !== goal.toLowerCase());
    onPreferencesChange({ goals: newGoals });
  };

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <FaList className="mr-2" /> Additional Preferences
      </h2>

      {/* Fitness Level */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Fitness Level
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {fitnessLevels.map(level => (
            <div
              key={level.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                preferences.fitness_level === level.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-200'
              }`}
              onClick={() => onPreferencesChange({ fitness_level: level.id })}
            >
              <h3 className="font-medium text-gray-900">{level.label}</h3>
              <p className="text-sm text-gray-500 mt-1">{level.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Goals */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700">
          Goals (select all that apply)
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {goalOptions.map(goal => (
            <label key={goal} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={preferences.goals.includes(goal.toLowerCase())}
                onChange={(e) => handleGoalChange(goal, e.target.checked)}
                className="rounded text-blue-500 focus:ring-blue-500"
              />
              <span>{goal}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Information */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Additional Information
        </label>
        <textarea
          value={preferences.additional_info}
          onChange={(e) => onPreferencesChange({ additional_info: e.target.value })}
          placeholder="Injuries, specific preferences or goals..."
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
    </div>
  );
};

Preferences.validate = (preferences) => {
  if (!preferences.fitness_level) {
    return 'Select your fitness level';
  }
  if (!preferences.goals || preferences.goals.length === 0) {
    return 'Select at least one goal';
  }
  return null;
};

export default CreateExercisePlan;